import Head from 'next/head';
import { memo, useMemo } from 'react';
import { getRuntimeConfig } from '~/shared/utils/config';
import { StoreSchemaObject } from './models';
import { usePage } from '~/templates/pages';
import { P71StoreDetailsPage, StoreImageSpotModule } from '~/lib';

type OpeningHoursSpecification = {
    '@type': 'OpeningHoursSpecification';
    dayOfWeek: string;
    opens: string;
    closes: string;
};

type StoreSchema = {
    '@context': 'https://schema.org';
    '@graph': Array<Record<string, unknown>>;
};

const dayMap: Record<string, string> = {
    Mandag: 'https://schema.org/Monday',
    Tirsdag: 'https://schema.org/Tuesday',
    Onsdag: 'https://schema.org/Wednesday',
    Torsdag: 'https://schema.org/Thursday',
    Fredag: 'https://schema.org/Friday',
    Lørdag: 'https://schema.org/Saturday',
    Søndag: 'https://schema.org/Sunday',
};

const IndividualStoreSchema = ({
    storeData,
}: {
    storeData: StoreSchemaObject[] | null | undefined;
}) => {
    const page = usePage() as P71StoreDetailsPage;
    const { breadcrumb } = page;

    const images = page.pageElements?.filter(
        (item): item is StoreImageSpotModule => item.type === 'storeImageSpot',
    );

    const { config } = getRuntimeConfig();
    const store = storeData?.[0] as StoreSchemaObject;

    const innerHTML: { __html: string } | undefined = useMemo(() => {
        if (!store?.id || !store.name) return undefined;

        const baseUrl = config.NEXT_PUBLIC_URL?.replace(/\/$/, '');
        const storeCurrentUrl = breadcrumb?.items?.at(-1)?.url;
        const storeUrl = `${baseUrl}${storeCurrentUrl}`;

        const storeId = `${store.id}#store`;

        const imageSrcs =
            images?.map((item) => item.image?.src).filter((src): src is string => Boolean(src)) ??
            [];

        const openingHours: OpeningHoursSpecification[] =
            store.c_openingHours
                ?.filter(
                    (openingHour) =>
                        !openingHour.isClosed &&
                        openingHour.opens &&
                        openingHour.closes &&
                        dayMap[openingHour.day],
                )
                .map((openingHour) => ({
                    '@type': 'OpeningHoursSpecification',
                    dayOfWeek: dayMap[openingHour.day],
                    opens: openingHour.opens!,
                    closes: openingHour.closes!,
                })) ?? [];

        const schemaObject: StoreSchema = {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'Organization',
                    '@id': `${baseUrl}#organization`,
                    name: 'Imerco',
                    url: baseUrl,
                },
                {
                    '@type': 'Store',
                    '@id': storeId,
                    name: store.name,
                    url: storeUrl,
                    ...(store.description && {
                        description: store.description,
                    }),
                    ...(store.phone && {
                        telephone: store.phone,
                    }),
                    ...(store.email && {
                        email: store.email,
                    }),
                    address: {
                        '@type': 'PostalAddress',
                        ...(store.address1 && {
                            streetAddress: store.address1,
                        }),
                        ...(store.postalCode && {
                            postalCode: store.postalCode,
                        }),
                        ...(store.city && {
                            addressLocality: store.city,
                        }),
                        ...(store.countryCode && {
                            addressCountry: store.countryCode,
                        }),
                    },
                    ...(openingHours.length > 0 && {
                        openingHoursSpecification: openingHours,
                    }),
                    ...(store.lat != null &&
                        store.lng != null && {
                            geo: {
                                '@type': 'GeoCoordinates',
                                latitude: store.lat,
                                longitude: store.lng,
                            },
                        }),
                    ...((imageSrcs.length > 0 || page.image?.src) && {
                        image: imageSrcs.length > 0 ? imageSrcs : [page.image?.src],
                    }),
                    parentOrganization: {
                        '@id': `${baseUrl}#organization`,
                    },
                },
            ],
        };

        return {
            __html: JSON.stringify(schemaObject),
        };
    }, [store, config.NEXT_PUBLIC_URL, images, page.image?.src]);

    if (!innerHTML) return null;

    return (
        <Head>
            <script type="application/ld+json" dangerouslySetInnerHTML={innerHTML} />
        </Head>
    );
};

export default memo(IndividualStoreSchema);
